import { Injectable } from '@angular/core';
import { NavigationExtras, Router, UrlTree } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { ToastrService } from 'ngx-toastr';

import { PodSearchMode, PodState } from '../../pods-validation/shared/pod-validation.model';
import { DocumentationAnchor, DocumentationTab } from '../../webhooks-events/webhook-documentation/webhook-documentation.component';

@Injectable()
export class NavigationService {

  constructor(private router: Router, private toastr: ToastrService, private translate: TranslateService) {
  }

  redirectToHomeIfNotAllowed(predicate: () => boolean): boolean | UrlTree {
    if (!predicate || !predicate()) {
      this.toastr.warning(this.translate.instant('nav.not-allowed'));
      return this.home().urlThree();
    }

    return true;
  }

  goTo(url: string, params?: NavigationExtras): void {
    Navigate.fromNavigation({ router: this.router, path: [ url ], params: params })
      .go();
  }

  login(params?: NavigationExtras) {
    return new AuthenticationNavigation(this.router).login(params);
  }

  logout(params?: NavigationExtras) {
    return new AuthenticationNavigation(this.router).logout(params);
  }

  home() {
    return new CoreNavigation(this.router).home();
  }

  overview() {
    return new CoreNavigation(this.router).overview();
  }

  regions() {
    return new RegionNavigation(this.router);
  }

  users() {
    return new UserNavigation(this.router);
  }

  administrators() {
    return new AdministratorNavigation(this.router);
  }

  chepManagers() {
    return new ChepManagerNavigation(this.router);
  }

  plantManagers() {
    return new PlantManagerNavigation(this.router);
  }

  chepServiceRep() {
    return new ChepServiceRepNavigation(this.router);
  }

  podReviewers() {
    return new PodReviewerNavigation(this.router);
  }

  sustainabilityManagers() {
    return new SustainabilityManagerNavigation(this.router);
  }

  overdueManagers() {
    return new OverdueManagerNavigation(this.router);
  }

  roles() {
    return new RoleNavigation(this.router);
  }

  permissions() {
    return new PermissionNavigation(this.router);
  }

  userSearch() {
    return new UserSearchNavigation(this.router);
  }

  carriers() {
    return new CarrierNavigation(this.router);
  }

  selfCarrier() {
    return new SelfCarrierNavigation(this.router);
  }

  hotspots() {
    return new HotspotNavigation(this.router);
  }

  realtimeTracking() {
    return new RealtimeTrackingNavigation(this.router);
  }

  forcedCheckin() {
    return new ForcedCheckinMonitorNavigation(this.router).monitor();
  }

  webhooksEvents() {
    return new WebhooksEventsNavigation(this.router);
  }

  truckJourneys() {
    return new TruckJourneysNavigation(this.router);
  }

  podsValidation() {
    return new PodsValidationNavigation(this.router);
  }

  localLoads() {
    return new LocalLoadsNavigation(this.router);
  }

  loadHandovers() {
    return new LoadHandoversNavigation(this.router);
  }

  logEvents(params?: NavigationExtras) {
    return new LogEventsNavigation(this.router).logEvents(params);
  }

  managePlants() {
    return new ManagePlantsNavigation(this.router).managePlants();
  }

  monitoring() {
    return new MonitoringNavigation(this.router);
  }

  reports() {
    return new ReportsNavigation(this.router);
  }

  reportsConfiguration() {
    return new ReportsConfigurationNavigation(this.router);
  }

  globalConfiguration() {
    return new ConfigurationNavigation(this.router);
  }

  assets() {
    return new AssetsNavigation(this.router);
  }

  overdue() {
    return new OverdueNavigation(this.router);
  }

  liteLogin() {
    return new LiteLoginNavigation(this.router);
  }

  loadsManagement() {
    return new LoadsManagementNavigation(this.router);
  }

  blueyonderUsers() {
    return new BlueyonderUsersNavigation(this.router);
  }

  tnxDashboard() {
    return new TnxDashboardNavigation(this.router);
  }

  carrierImport() {
    return new CarrierImportNavigation(this.router);
  }

  dfMasterDataManagement() {
    return new DFMasterDataNavigation(this.router);
  }

  dFDashboard() {
    return new DFMasterDataNavigation(this.router);
  }

  dfTransactionManagemnt() {
    return new DFTransactionNavigation(this.router);
  }
  decarbonizationConfiguration() {
    return new DecarbonizationNavigation(this.router);
  }

  dfAdminManagemnt() {
    return new DFAdminNavigation(this.router);
  }
  dfVariableChargesManagemnt() {
    return new DFAdminNavigation(this.router);
  }

  calendarScreenNavigate() {
    return new DFSchedularNavigation(this.router);
  }

}

class Navigation {

  path: string[]           = [];
  params: NavigationExtras = {};
  router: Router;

  constructor(router: Router) {
    this.router = router;
  }
}

class Navigate extends Navigation implements Navigable {

  static fromNavigation(navigation: Navigation) {
    const navigate: Navigate = new Navigate(navigation.router);
    navigate.path            = navigation.path;
    navigate.params          = navigation.params;

    return navigate;
  }

  constructor(router: Router) {
    super(router);
  }

  go() {
    this.router.navigate(this.path, this.params)
      .catch(error => console.log('navigation error => ', error));
  }

  getUrl(): string {
    const domain = window.location.origin;
    const uri    = this.getUri();
    return `${domain}/${uri}`;
  }

  getUri(): string {
    const urlTree = this.router.createUrlTree(this.path, this.params);
    return urlTree.toString();
  }

  urlThree(): UrlTree {
    return this.router.parseUrl(this.getUrl());
  }
}

class CrudNavigation extends Navigation {

  constructor(private pathPrefix, router: Router) {
    super(router);
  }

  list(params?: NavigationExtras): Navigable {
    this.path = this.pathPrefix;
    this.params = params;
    return Navigate.fromNavigation(this);
  }

  create(): Navigable {
    this.path = this.pathPrefix.concat(['new']);
    return Navigate.fromNavigation(this);
  }

  edit(id: string): Navigable {
    this.path = this.pathPrefix.concat(['edit', id]);
    return Navigate.fromNavigation(this);
  }
}

class AuthenticationNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  login(params?: NavigationExtras): Navigable {
    this.path   = ['']; // TODO: Pas sur du path ici, ou de s'il faut garder
    this.params = params;
    return Navigate.fromNavigation(this);
  }

  logout(params?: NavigationExtras): Navigable {
    this.path   = ['logout'];
    this.params = params;
    return Navigate.fromNavigation(this);
  }
}

class CoreNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  home(): Navigable {
    this.path = ['home'];
    return Navigate.fromNavigation(this);
  }

  overview(): Navigable {
    this.path = ['userdashboard', 'overview'];
    return Navigate.fromNavigation(this);
  }
}

class MonitoringNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  status(): Navigable {
    this.path = ['monitoring', 'status'];
    return Navigate.fromNavigation(this);
  }

  http(): Navigable {
    this.path = ['monitoring', 'http'];
    return Navigate.fromNavigation(this);
  }
}

class LogEventsNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  logEvents(params?: NavigationExtras): Navigable {
    this.path = ['log-events'];
    this.params = params;
    return Navigate.fromNavigation(this);
  }
}

class ManagePlantsNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  managePlants(): Navigable {
    this.path = ['manage-plants'];
    return Navigate.fromNavigation(this);
  }
}

class RegionNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'regions'], router);
  }
}

class UserNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'users'], router);
  }
}

class AdministratorNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'administrators'], router);
  }
}

class ChepManagerNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'chepManagers'], router);
  }

  createDuplicate(allowedRegions: string[]): Navigable {
    this.path = ['userdashboard', 'chepManagers', 'new'];
    this.params = { queryParams: {allowedRegions: allowedRegions} };
    return Navigate.fromNavigation(this);
  }
}

class PlantManagerNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'plantManagers'], router);
  }
}

class ChepServiceRepNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'chepServiceRep'], router);
  }
}

class PodReviewerNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'podReviewers'], router);
  }
}

class SustainabilityManagerNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'sustainabilityManagers'], router);
  }
}

class OverdueManagerNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'overdueManagers'], router);
  }
}

class RoleNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'roles'], router);
  }
}

class PermissionNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'permissions'], router);
  }
}

class UserSearchNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'userSearch'], router);
  }
}

class CarrierNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  list(params?: NavigationExtras): Navigable {
    this.path = ['userdashboard', 'carriers'];
    this.params = params;
    return Navigate.fromNavigation(this);
  }

  create(): Navigable {
    this.path = ['userdashboard', 'carriers', 'new'];
    return Navigate.fromNavigation(this);
  }

  edit(carrierId: string): Navigable {
    this.path = ['userdashboard', 'carriers', 'edit', carrierId];
    return Navigate.fromNavigation(this);
  }

  managers(carrierId: string): CarrierManagerNavigation {
    return new CarrierManagerNavigation(carrierId, this.router);
  }

  drivers(carrierId: string): CarrierDriverNavigation {
    return new CarrierDriverNavigation(carrierId, this.router);
  }

  subcontractors(carrierId: string): SubcontractorNavigation {
    return new SubcontractorNavigation(carrierId, this.router);
  }
}

class SelfCarrierNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  editOwn(): Navigable {
    this.path = ['userdashboard', 'carrier', 'edit'];
    return Navigate.fromNavigation(this);
  }

  carrierManager() {
    return new SelfCarrierManagerNavigation(this.router);
  }

  drivers() {
    return new SelfCarrierDriverNavigation(this.router);
  }

  subcontractors(): SelfSubcontractorNavigation {
    return new SelfSubcontractorNavigation(this.router);
  }

  blueyonderUsers(): SelfBlueyonderUsersNavigation {
    return new SelfBlueyonderUsersNavigation(this.router);
  }
}

class CarrierManagerNavigation extends CrudNavigation {

  constructor(private carrierId: string, router: Router) {
    super(['userdashboard', 'carriers', carrierId, 'managers'], router);
  }
}

class SubcontractorNavigation extends CrudNavigation {

  constructor(private carrierId: string, router: Router) {
    super(['userdashboard', 'carriers', carrierId, 'subcontractors'], router);
  }

  drivers(subcontractorId: string): SubcontractorDriverNavigation {
    return new SubcontractorDriverNavigation(this.carrierId, subcontractorId, this.router);
  }
}

class SelfSubcontractorNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'carrier', 'subcontractors'], router);
  }

  drivers(subcontractorId: string): SelfCarrierSubcontractorDriverNavigation {
    return new SelfCarrierSubcontractorDriverNavigation(subcontractorId, this.router);
  }
}

class SelfCarrierManagerNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'carrier', 'carrier-manager'], router);
  }
}

class SelfCarrierDriverNavigation extends CrudNavigation {

  constructor(router: Router) {
    super(['userdashboard', 'carrier', 'drivers'], router);
  }
}

class CarrierDriverNavigation extends CrudNavigation {

  constructor(private carrierId: string, router: Router) {
    super(['userdashboard', 'carriers', carrierId, 'drivers'], router);
  }
}

class SubcontractorDriverNavigation extends CrudNavigation {

  constructor(private carrierId: string, private subcontractorId: string, router: Router) {
    super(['userdashboard', 'carriers', carrierId, 'subcontractors', subcontractorId, 'drivers'], router);
  }
}

class SelfCarrierSubcontractorDriverNavigation extends CrudNavigation {

  constructor(private subcontractorId: string, router: Router) {
    super(['userdashboard', 'carrier', 'subcontractors', subcontractorId, 'drivers'], router);
  }
}

class HotspotNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  list(params?: NavigationExtras): Navigable {
    this.path = ['hotspots', 'list'];
    this.params = params;
    return Navigate.fromNavigation(this);
  }

  alerts(): Navigable {
    this.path = ['hotspots', 'alerts'];
    return Navigate.fromNavigation(this);
  }

  map(hotspotId?: string): Navigable {
    this.path = ['hotspots', 'map'];
    if (hotspotId) {
      this.path.push(hotspotId);
    }

    return Navigate.fromNavigation(this);
  }
}

class RealtimeTrackingNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  overview(carrierId?: string, tripId?: string): Navigable {
    this.path   = ['realtime-tracking', 'overview'];
    this.params = { queryParams: { carrierId, tripId } };
    return Navigate.fromNavigation(this);
  }

  map(carrierId?: string, tripId?: string): Navigable {
    this.path   = ['realtime-tracking', 'map'];
    this.params = { queryParams: { carrierId, tripId } };
    return Navigate.fromNavigation(this);
  }

  trackATrip(tripId: string): Navigable {
    this.path = ['customer', 'track-a-trip', tripId];
    return Navigate.fromNavigation(this);
  }
}

class ForcedCheckinMonitorNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  monitor(): Navigable {
    this.path = ['forced-checkin'];
    return Navigate.fromNavigation(this);
  }
}

class WebhooksEventsNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  overall(carrierId?: string): Navigable {
    this.path   = ['webhooks-events'];
    this.params = { queryParams: { carrierId } };
    return Navigate.fromNavigation(this);
  }

  details(eventId: string): Navigable {
    this.path   = ['webhooks-events', 'details', eventId];
    return Navigate.fromNavigation(this);
  }

  config(): Navigable {
    this.path   = ['webhooks-events', 'config'];
    return Navigate.fromNavigation(this);
  }

  doc(tab?: DocumentationTab, anchor?: DocumentationAnchor): Navigable {
    this.path   = ['webhooks-events', `doc`];
    if (tab || anchor) {
      this.params = { queryParams: { tab, anchor } };
    }
    return Navigate.fromNavigation(this);
  }
}

class TruckJourneysNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  search(carrierId?: string, regionId?: string): Navigable {
    this.path   = ['truck-journeys'];
    this.params = { queryParams: { carrierId, regionId } };
    return Navigate.fromNavigation(this);
  }

  detail(truckJourneyId: string, carrierId?: string, assignLoads?: boolean): Navigable {
    this.path   = ['truck-journeys', truckJourneyId, 'detail'];
    this.params = { queryParams: { carrierId, assignLoads } };
    return Navigate.fromNavigation(this);
  }

  create(carrierId?: string): Navigable {
    this.path   = ['truck-journeys', 'create'];
    this.params = { queryParams: { carrierId } };
    return Navigate.fromNavigation(this);
  }

  loadAssignment(carrierId?: string): Navigable {
    this.path   = ['truck-journeys', 'load-assignment'];
    this.params = { queryParams: { carrierId } };
    return Navigate.fromNavigation(this);
  }
}

class PodsValidationNavigation extends Navigation {
  constructor(router: Router) {
    super(router);
  }

  overall(mode?: PodSearchMode, state?: PodState, regionIds?: Array<string>): Navigable {
    this.path   = ['pods-validation'];
    this.params = { queryParams: { mode, state, regionIds, }}
    return Navigate.fromNavigation(this);
  }
}

class LocalLoadsNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  import(carrierId?: string): Navigable {
    this.path   = ['local-loads', 'import'];
    this.params = { queryParams: { carrierId } };
    return Navigate.fromNavigation(this);
  }

  find(): Navigable {
    this.path = ['local-loads', 'find'];
    return Navigate.fromNavigation(this);
  }

  reorder(): Navigable {
    this.path = ['local-loads', 'reorder'];
    return Navigate.fromNavigation(this);
  }

  locations(params?: NavigationExtras): Navigable {
    this.path = ['local-loads', 'locations'];
    this.params = params;
    return Navigate.fromNavigation(this);
  }

  manual(): Navigable {
    this.path = ['local-loads', 'manual', 'creation'];
    return Navigate.fromNavigation(this);
  }
}

class LoadHandoversNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  map(): Navigable {
    this.path = ['load-handovers', 'map'];
    return Navigate.fromNavigation(this);
  }
}

class ReportsNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['reports', 'power-bi'];
    return Navigate.fromNavigation(this);
  }

}

class AssetsNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['reports', 'assets'];
    return Navigate.fromNavigation(this);
  }

}

class OverdueNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['overdue'];
    return Navigate.fromNavigation(this);
  }

}

class LiteLoginNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['lite-login', 'accesses'];
    return Navigate.fromNavigation(this);
  }

  create(): Navigable {
    this.path = ['lite-login', 'new'];
    return Navigate.fromNavigation(this);
  }

  edit(id: string): Navigable {
    this.path = ['lite-login', 'edit', id];
    return Navigate.fromNavigation(this);
  }

  see(id: string): Navigable {
    this.path = ['lite-login', 'see', id];
    return Navigate.fromNavigation(this);
  }
}

class LoadsManagementNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  searchLoads(params?: NavigationExtras): Navigable {
    this.path = ['bluechat'];
    this.params = params;
    return Navigate.fromNavigation(this);
  }

  detail(loadId: string): Navigable {
    this.path = ['bluechat', loadId]
    return Navigate.fromNavigation(this);
  }

}

class ReportsConfigurationNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['reports', 'configuration'];
    return Navigate.fromNavigation(this);
  }

  create(): Navigable {
    this.path = ['reports', 'new'];
    return Navigate.fromNavigation(this);
  }

  edit(reportId: string): Navigable {
    this.path = ['reports', 'edit', reportId];
    return Navigate.fromNavigation(this);
  }

}

class DecarbonizationNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['decarbonization'];
    return Navigate.fromNavigation(this);
  }
}

class BlueyonderUsersNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  list(carrierId?: string): Navigable {
    this.path = ['userdashboard', 'blueyonder', 'users'];
    this.params = { queryParams: { carrierId } };
    return Navigate.fromNavigation(this);
  }

  create(carrierId?: string): Navigable {
    this.path = ['userdashboard', 'blueyonder', 'users', 'new'];
    this.params = { queryParams: { carrierId } };
    return Navigate.fromNavigation(this);
  }
}

class SelfBlueyonderUsersNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['userdashboard', 'carrier', 'blueyonder', 'users'];
    return Navigate.fromNavigation(this);
  }

  create(): Navigable {
    this.path = ['userdashboard', 'carrier', 'blueyonder', 'users', 'new'];
    return Navigate.fromNavigation(this);
  }
}

class ConfigurationNavigation extends Navigation {

  constructor(router: Router) {
    super(router);
  }

  support(): Navigable {
    this.path = ['configuration', 'support'];
    return Navigate.fromNavigation(this);
  }

  cicoHelp(): Navigable {
    this.path = ['configuration', 'cico-help'];
    return Navigate.fromNavigation(this);
  }
}

class TnxDashboardNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['tnx-dashboard'];
    return Navigate.fromNavigation(this);
  }

}

class CarrierImportNavigation extends Navigate {
  constructor(router: Router) {
    super(router);
  }

  verificationTool(): Navigable {
    this.path = ['carrier-import', 'verification-tool'];
    return Navigate.fromNavigation(this);
  }
}

class DFMasterDataNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['df-management', 'list'];
    return Navigate.fromNavigation(this);
  }

}

class DFTransactionNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['df-management', 'trans-action-list'];
    return Navigate.fromNavigation(this);
  }
}

class DFAdminNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['df-management', 'admin'];
    return Navigate.fromNavigation(this);
  }

  adminlist(): Navigable {
    this.path = ['df-management', 'country-list'];
    return Navigate.fromNavigation(this);
  }

  chargelist(): Navigable {
    this.path = ['df-management', 'variableCharges-list'];
    return Navigate.fromNavigation(this);
  }

  calendarList(): Navigable {
    this.path = ['df-management', 'calendar-list'];
    return Navigate.fromNavigation(this);
  }

  addCalendar(): Navigable {
    this.path = ['df-management', 'add-calendar'];
    return Navigate.fromNavigation(this);
  }

  addCountryForm(): Navigable {
    this.path = ['df-management', 'add-country-form'];
    return Navigate.fromNavigation(this);
  }
}

class DFSchedularNavigation extends Navigate {

  constructor(router: Router) {
    super(router);
  }

  list(): Navigable {
    this.path = ['df-management', 'logs-list'];
    return Navigate.fromNavigation(this);
  }
}


export interface Navigable {

  go();

  getUrl(): string;

  getUri(): string;

  urlThree(): UrlTree;
}
